import { useState, useCallback } from 'react';
import walletSyncService from '../services/walletSyncService';
import api from '../services/api';
import iconService from '../services/iconService';
import { useTranslation } from 'react-i18next';
import { TRANSACTIONS_ENABLED } from '../config/featureFlags';

export const useWalletSync = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedBalances, setGroupedBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [syncProgress, setSyncProgress] = useState(null);
  const [transactionPagination, setTransactionPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 50,
    totalCount: 0
  });

  /**
   * Fetch grouped balances for all wallets
   */
  const refreshGroupedBalances = useCallback(async (options = {}) => {
    try {
      const balances = await walletSyncService.getGroupedBalances(options);
      setGroupedBalances(balances);
      return balances;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Fetch transactions with pagination
   */
  const fetchTransactions = useCallback(async (limit = 50, offset = 0, filters = {}) => {
    try {
      const result = await walletSyncService.getTransactions(limit, offset, filters);
      setTransactions(result.transactions);
      setTransactionPagination({
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        currentPage: Math.floor(offset / limit) + 1,
        limit
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get all wallets
   */
  const getWallets = useCallback(async (options = {}) => {
    try {
      return await walletSyncService.getWallets(options);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Sync a specific wallet by its ID
   */
  const syncWallet = useCallback(async (walletId, userId, address, blockchain) => {
    setLoading(true);
    setError(null);
    setSyncProgress({ walletId, status: 'starting', message: 'tables.wallets.sync.starting' });
    console.log(`[useWalletSync] Starting sync for walletId=${walletId}, blockchain=${blockchain}`);
    try {
      const response = await api.post(`/api/wallets/${walletId}/sync`);
      console.log(`[useWalletSync] /api/wallets/${walletId}/sync response:`, response.data);
      if (response.data.success) {
        const refreshes = [refreshGroupedBalances()];
        if (TRANSACTIONS_ENABLED) refreshes.push(fetchTransactions(50, 0, {}));
        await Promise.all(refreshes);
        await new Promise(resolve => setTimeout(resolve, 1000));
        await iconService.refreshNewCoins();
        try {
          await api.post('/api/coins/update-icons');
        } catch (error) {
          console.warn(`[useWalletSync] Background icon refresh failed:`, error.message);
        }
        setSyncProgress({ walletId, status: 'completed', message: 'Sync completed successfully' });
        console.log(`[useWalletSync] Sync completed for walletId=${walletId}, blockchain=${blockchain}`);
        setTimeout(() => {
          setSyncProgress(null);
        }, 3000);
      } else {
        console.error(`[useWalletSync] Sync failed for walletId=${walletId}, blockchain=${blockchain}:`, response.data.message);
        throw new Error(response.data.message || 'Sync failed');
      }
    } catch (err) {
      console.error(`[useWalletSync] Error syncing wallet ${walletId}, blockchain=${blockchain}:`, err);
      let errorMsg = err.response?.data?.error || err.message || 'Sync failed';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setError(errorMsg);
      setSyncProgress({ walletId, status: 'error', message: errorMsg });
      console.log(`[useWalletSync] Sync error for walletId=${walletId}, blockchain=${blockchain}:`, errorMsg);
    } finally {
      setLoading(false);
    }
  }, [refreshGroupedBalances, fetchTransactions]);

  /**
   * Add a new wallet with enhanced sync
   */
  const addWallet = useCallback(async (walletData) => {
    setLoading(true);
    setError(null);
    setSyncProgress({ status: 'starting', message: 'Adding wallet...' });

    try {
      // console.log(`[useWalletSync] Adding wallet:`, walletData);
      
      const response = await api.post('/api/wallets', walletData);
      
      if (response.data.wallet) {
        // console.log(`[useWalletSync] Wallet added successfully:`, response.data.wallet.id);

        const refreshes = [refreshGroupedBalances()];
        if (TRANSACTIONS_ENABLED) refreshes.push(fetchTransactions(50, 0, {}));
        await Promise.all(refreshes);
        
        // Wait a moment for server to process new coins and icons
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh icons to ensure new coins have their icons loaded
        // console.log(`[useWalletSync] Refreshing icons after wallet addition...`);
        await iconService.refreshNewCoins();
        
        // Trigger background icon refresh for any missing icons
        try {
          // console.log(`[useWalletSync] Triggering background icon refresh...`);
          await api.post('/api/coins/update-icons');
          // console.log(`[useWalletSync] Background icon refresh triggered successfully`);
        } catch (error) {
          console.warn(`[useWalletSync] Background icon refresh failed:`, error.message);
        }
        
        setSyncProgress({ status: 'completed', message: 'Wallet added successfully' });
        
        // Clear progress after a delay
        setTimeout(() => {
          setSyncProgress(null);
        }, 3000);
        
        return response.data;
      } else {
        throw new Error('Failed to add wallet');
      }
    } catch (err) {
      console.error(`[useWalletSync] Error adding wallet:`, err);
      let errorMsg = err.response?.data?.error || err.message || 'Failed to add wallet';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setError(errorMsg);
      setSyncProgress({ status: 'error', message: errorMsg });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshGroupedBalances, fetchTransactions]);

  /**
   * Delete a wallet
   */
  const deleteWallet = useCallback(async (walletId) => {
    try {
      const result = await walletSyncService.deleteWallet(walletId);

      const refreshes = [refreshGroupedBalances()];
      if (TRANSACTIONS_ENABLED) refreshes.push(fetchTransactions(50, 0, {}));
      await Promise.all(refreshes);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [refreshGroupedBalances, fetchTransactions]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear sync progress
   */
  const clearSyncProgress = useCallback(() => {
    setSyncProgress(null);
  }, []);

  /**
   * Clear grouped balances state
   */
  const clearGroupedBalances = useCallback(() => {
    setGroupedBalances([]);
  }, []);

  return {
    // State
    loading,
    error,
    groupedBalances,
    transactions,
    syncProgress,
    transactionPagination,
    
    // Actions
    syncWallet,
    addWallet,
    refreshGroupedBalances,
    fetchTransactions,
    getWallets,
    deleteWallet,
    clearError,
    clearSyncProgress,
    clearGroupedBalances,
    setTransactions,
    setSyncProgress, // Export setSyncProgress for external use
  };
}; 